/**
 * The address step's two decisions, shared by both booking funnels.
 *
 * `StepThree` and `TaskContactInfo` both call `verifyAddress`, and they had
 * drifted into two copies of the same wrong rule: each refused on `isValid`
 * alone under a hardcoded three-borough sentence, and each appended the ZIP to
 * the address by string concatenation. Fixing one page left the other letting a
 * Bronx address through. One module, called by both, is what keeps them the
 * same funnel.
 */

/** The fields of `verifyAddress` these decisions read. */
export interface VerifiedAddress {
  isValid: boolean;
  zip: string;
  serviceable: boolean;
  serviceAreaLabel: string;
}

export interface AddressRefusal {
  title: string;
  description: string;
}

/**
 * Why this address cannot be booked, or null when it can.
 *
 * Two refusals, because they are two different problems and the customer can
 * only act on one of them. Telling someone in a borough we do not cover to
 * "provide a valid address" sends them to re-type an address that was never the
 * problem.
 *
 * `serviceable` is the server answering the question this step actually asks,
 * against the `service_area` table. `isValid` is only NYC Geoclient saying the
 * address exists — true for all five boroughs — which is why refusing on it
 * alone let the Bronx book.
 */
export function addressRefusal(
  verified: VerifiedAddress | null | undefined
): AddressRefusal | null {
  if (!verified?.isValid) {
    return {
      title: "We could not find that address",
      description: "Please check the street number and name, then try again.",
    };
  }
  if (!verified.serviceable) {
    return {
      title: "We don’t clean at this address yet",
      /**
       * The borough list comes from the same rows the check reads, so this
       * sentence cannot drift from the policy the way the hardcoded one did.
       * Empty when the server could not read them, in which case we say less
       * rather than something we cannot stand behind.
       */
      description: verified.serviceAreaLabel
        ? `We clean in ${verified.serviceAreaLabel}.`
        : "That address is outside the area we clean.",
    };
  }
  return null;
}

/**
 * The address as the confirmation email and the cleaner's job sheet print it:
 * what the customer typed, with the verified ZIP on the end.
 *
 * Composed at the point the booking is sent rather than written back into the
 * address field, so the field keeps showing exactly what was typed and going
 * back a step cannot stack a second ZIP onto it — which is how the old
 * `setAddress(address + ", " + zip)` shipped "123 Main St, 11215, 11215". A ZIP
 * the customer typed themselves is replaced rather than repeated.
 */
export function withZip(address: string, zip: string | null | undefined): string {
  const typed = address.trim();
  if (!zip) return typed;
  return `${typed.replace(/,?\s*[0-9]{5}$/, "")}, ${zip}`;
}
